import axios from 'axios';
import { msalInstance, loginRequest } from '@/config/msalConfig';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - adds auth token (MSAL or local JWT)
 */
apiClient.interceptors.request.use(
  async (config) => {
    let token: string | null = null;

    // Try MSAL (Entra ID) first
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        token = response.accessToken;
      } catch {
        // Silent acquisition failed - fall through to local token
      }
    }

    // Fall back to local JWT
    if (!token) {
      token = localStorage.getItem('auth_token');
    }

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  },
);

/**
 * Response interceptor - handles 401 and formats errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.message;

      console.error(`[API Error ${status}]`, message);

      if (status === 401) {
        localStorage.removeItem('auth_token');
        // Redirect to login unless already there
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }

      error.statusCode = status;
      error.errorMessage = message;
    } else if (error.request) {
      console.error('[API Network Error]', error.request);
      error.errorMessage = 'Network error - please check your connection';
    } else {
      console.error('[API Setup Error]', error.message);
      error.errorMessage = error.message;
    }

    return Promise.reject(error);
  },
);

export const api = {
  get: <T>(url: string, config?: Parameters<typeof apiClient.get>[1]) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.post>[2]) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.put>[2]) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.patch>[2]) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: Parameters<typeof apiClient.delete>[1]) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};
